import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Clock, Shield, Users, Zap } from 'lucide-react';

const VisionSection: React.FC = () => {
  const { t } = useTranslation('landing');

  const visionItems = [
    {
      icon: Clock,
      title: t('vision.items.hoursMatter.title'),
      description: t('vision.items.hoursMatter.description'),
      color: 'text-accent-500'
    },
    {
      icon: Shield,
      title: t('vision.items.dataDriven.title'),
      description: t('vision.items.dataDriven.description'),
      color: 'text-ocean-500'
    },
    {
      icon: Users,
      title: t('vision.items.fieldCentered.title'),
      description: t('vision.items.fieldCentered.description'),
      color: 'text-sage-500'
    },
    {
      icon: Zap,
      title: t('vision.items.rapidDeployment.title'),
      description: t('vision.items.rapidDeployment.description'),
      color: 'text-ink-500'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-sand-50 to-white">
      <div className="container-grid">
        <div className="grid-12">
          <div className="col-span-12 text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display font-bold text-ink-900 mb-6 uppercase">
                {t('vision.title')}
              </h2>
              <p className="text-xl text-ink-600 max-w-4xl mx-auto">
                {t('vision.subtitle')}
              </p>
            </motion.div>
          </div>

          <div className="col-span-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {visionItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="mb-6 relative">
                  <div className="w-20 h-20 mx-auto bg-white border-2 border-ink-100 flex items-center justify-center group-hover:border-accent-300 transition-colors">
                    <item.icon className={`w-10 h-10 ${item.color}`} />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-ink-900 mb-4">{item.title}</h3>
                <p className="text-ink-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="col-span-12 mt-16 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white p-8 border-2 border-ink-100 max-w-6xl mx-auto"
            >
              <h3 className="text-2xl font-bold text-ink-900 mb-4">
                {t('vision.comparison.title')}
              </h3>
              <p className="text-lg text-ink-600 mb-8">
                {t('vision.comparison.subtitle')}
              </p>
              
              {/* Comparison Visualization */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Traditional Approach */}
                <div className="space-y-4">
                  <h4 className="text-lg font-bold text-accent-600 mb-4 uppercase">{t('vision.comparison.traditional.title')}</h4>
                  <div className="space-y-3">
                    {[
                      { step: t('vision.comparison.traditional.steps.step1'), time: t('vision.comparison.traditional.timing.day1') },
                      { step: t('vision.comparison.traditional.steps.step2'), time: t('vision.comparison.traditional.timing.day2') },
                      { step: t('vision.comparison.traditional.steps.step3'), time: t('vision.comparison.traditional.timing.day3') },
                      { step: t('vision.comparison.traditional.steps.step4'), time: t('vision.comparison.traditional.timing.day4') },
                      { step: t('vision.comparison.traditional.steps.step5'), time: t('vision.comparison.traditional.timing.day5') }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-ink-50 border border-ink-200">
                        <div className="w-8 h-8 bg-ink-600 text-white text-sm font-mono flex items-center justify-center">
                          {i + 1}
                        </div>
                        <span className="text-sm text-ink-700">{item.step}</span>
                        <div className="ml-auto text-xs text-ink-500">
                          {item.time}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-center mt-4 p-3 bg-ink-100 border border-ink-300">
                    <span className="text-sm font-bold text-ink-700 uppercase">{t('vision.comparison.traditional.responseTime')}</span>
                  </div>
                </div>

                {/* CLEAR Approach */}
                <div className="space-y-4">
                  <h4 className="text-lg font-bold text-sage-600 mb-4 uppercase">{t('vision.comparison.withClear.title')}</h4>
                  <div className="space-y-3">
                    {[
                      { step: t('vision.comparison.withClear.steps.step1'), time: t('vision.comparison.withClear.timing.hour1') },
                      { step: t('vision.comparison.withClear.steps.step2'), time: t('vision.comparison.withClear.timing.hour2') },
                      { step: t('vision.comparison.withClear.steps.step3'), time: t('vision.comparison.withClear.timing.hour3') },
                      { step: t('vision.comparison.withClear.steps.step4'), time: t('vision.comparison.withClear.timing.hour4') },
                      { step: t('vision.comparison.withClear.steps.step5'), time: t('vision.comparison.withClear.timing.hour5') }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-sage-50 border border-sage-200">
                        <div className="w-8 h-8 bg-sage-500 text-white text-sm font-mono flex items-center justify-center">
                          {i + 1}
                        </div>
                        <span className="text-sm text-sage-700">{item.step}</span>
                        <div className="ml-auto text-xs text-sage-600">
                          {item.time}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-center mt-4 p-3 bg-sage-100 border border-sage-300">
                    <span className="text-sm font-bold text-sage-700 uppercase">{t('vision.comparison.withClear.responseTime')}</span>
                  </div>
                </div>
              </div>

              {/* Key Improvements */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center p-4 bg-accent-50 border border-accent-200">
                  <div className="text-2xl font-bold text-accent-600 mb-2">96%</div>
                  <div className="text-sm text-accent-700 uppercase font-medium">{t('vision.comparison.improvements.faster')}</div>
                </div>
                <div className="text-center p-4 bg-ocean-50 border border-ocean-200">
                  <div className="text-2xl font-bold text-ocean-600 mb-2">85%</div>
                  <div className="text-sm text-ocean-700 uppercase font-medium">{t('vision.comparison.improvements.lessGuesswork')}</div>
                </div>
                <div className="text-center p-4 bg-sage-50 border border-sage-200">
                  <div className="text-2xl font-bold text-sage-600 mb-2">10x</div>
                  <div className="text-sm text-sage-700 uppercase font-medium">{t('vision.comparison.improvements.moreData')}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionSection;